import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CloseIcon from '@/src/assets/svgs/CloseIcon'
import { NearbyAirportPopupData } from '@/src/hooks/srp/nearbyAirportAlertManager'
import { FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { Box, Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import { FC } from 'react'

interface NearbyAirportPopupProps {
    open: boolean
    onClose: () => void
    onContinue: (flight: FlightCard) => void
    data?: NearbyAirportPopupData | null
    flight?: FlightCard | null
    jsons?: JsonsData
    sectors?: Sectors
}

const NearbyAirportPopup: FC<NearbyAirportPopupProps> = ({ open, onClose, onContinue, data }) => {
    const tSrp = useTranslations('srpFlightDetails')
    const locale = useLocale();
    const isArabic = locale === 'ar';
    if (!data) return null
    const { airlineName, departureTime, arrivalTime, displayAirportName, flight } = data

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md" 
            slotProps={{
                paper: { sx: { borderRadius: 1.5, m: "8px", boxShadow: 12, maxWidth: '480px' } }
            }}
        >
            <DialogContent sx={{ p: {sm : 1.5, md : 1.8}, position: 'relative' }}>

                <Stack spacing={2}>
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={"0.75rem"}>
                            <Typography variant="h6" fontWeight={600} 
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
>
                                {tSrp('nearbyAirportPopup.title')}
                            </Typography>
                        </Box>
                        {/* Warning Box */}
                        <Box
                            sx={{
                                bgcolor: '#fff7ed', // bg-orange-50
                                border: '1px solid #fed7aa', // border-orange-200
                                borderRadius: 2,
                                p: 1.5,
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'center'
                            }}
                        >
                            <WarningAmberIcon sx={{ color: '#ea580c', fontSize: 24 }} />
                            <Typography variant="body2" fontWeight={500} color="#9a3412"> {/* text-orange-800 */}
                                {tSrp('nearbyAirportPopup.warning')}
                            </Typography>
                        </Box>

                        {/* Flight Details & Info */}
                        <Stack spacing={1.5} sx={{ mt: 2, color: 'text.secondary' }}>
                            <Typography variant="body2" color="text.primary">
                                <strong>{tSrp('flightDetails')}:</strong> {airlineName} {departureTime} {isArabic ? '←' : '→'} {arrivalTime}
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                <strong>{tSrp('nearbyAirportPopup.airportLabel')}</strong> {tSrp('nearbyAirportPopup.via', { airport: displayAirportName })}
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Actions */}
                    <Stack
                        direction={{ xs: 'column-reverse', sm: 'row' }}
                        justifyContent="flex-end"
                        gap={1.5}
                        sx={{ mt: 2 }}
                    >
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            fullWidth
                            sx={{
                                color: 'text.primary',
                                borderColor: 'divider',
                                textTransform: 'none',
                                fontWeight: 500,
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                    borderColor: 'divider'
                                }
                            }}
                        >
                            {tSrp('nearbyAirportPopup.cancel')}
                        </Button>
                        <Button
                            variant="customVariantPrimary"
                            onClick={() => onContinue(flight)}
                            fullWidth
                            sx={{
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                        >
                            {tSrp('nearbyAirportPopup.continueBooking')}
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export default NearbyAirportPopup